/**
 * Engraving feature for Kalles (instax-live)
 * Ported from the Dawn `mini12camera-product` section.
 *
 * Self-contained: activates only when `[data-engrave-root]` (rendered by
 * snippets/engraving.liquid) is present on the page. Does nothing otherwise,
 * so it is safe to ship globally — it only ever runs on engraving templates.
 *
 * Couplings into Kalles:
 *   - variant change : listens to jQuery `variant:change` on document
 *   - add to cart     : when engraving is saved, silently pre-adds a SEPARATE
 *                       engraving line item, then re-fires Kalles' own ATC so
 *                       Kalles handles the drawer refresh/open natively.
 *   - cart monitor    : on Kalles' CartUpdateSuccess, removes the orphan
 *                       engraving line if the main product left the cart.
 */
(function () {
  'use strict';

  var $K = window.jQuery_T4NT || window.jQuery || window.$ || null;
  var activeController = null;
  // While an add-to-cart is in flight the engraving line exists a moment before its
  // main product, so it briefly looks "orphaned". The guard defers removal until this
  // timestamp passes (set by the add-to-cart handler).
  var addGraceUntil = 0;

  function init() {
    // Global guard — runs on every page from the config injected in theme.liquid.
    var cfg = readConfig();
    console.log('[Engrave] script loaded. config =', cfg);
    if (cfg) { try { new CartGuard(cfg); } catch (e) { console.error('[Engrave] guard init', e); } }
    else { console.warn('[Engrave] no #engraving-config found — is theme.liquid pushed?'); }

    var roots = document.querySelectorAll('[data-engrave-root]');
    if (!roots.length) return;

    // Kalles' main-product outputs section blocks through BOTH product columns
    // (pr_summary = .leftSide, pr_summary1 = .rightSide), so our custom_liquid
    // engraving block is emitted twice. Keep ONE — the one in the buy column.
    var keep = null;
    Array.prototype.forEach.call(roots, function (r) {
      if (!keep && r.closest && r.closest('.rightSide')) keep = r;
    });
    if (!keep) keep = roots[0];
    Array.prototype.forEach.call(roots, function (r) {
      if (r !== keep && r.parentNode) r.parentNode.removeChild(r);
    });

    // Place the single instance directly below the variant selector.
    repositionBelowVariant(keep);

    if (keep.__engraveBound) return;
    keep.__engraveBound = true;
    try { new EngraveController(keep); } catch (e) { console.error('[Engrave] init error', e); }
  }

  // Move the engraving block to sit right under the variant swatches in the buy column.
  function repositionBelowVariant(root) {
    var scope = (root.closest && root.closest('.rightSide')) || document.querySelector('.rightSide') || document;
    var anchor = scope.querySelector('.variations') || scope.querySelector('.swatches-select');
    if (anchor && anchor.parentNode && anchor.nextElementSibling !== root) {
      anchor.insertAdjacentElement('afterend', root);
    }
  }

  function EngraveController(root) {
    this.root = root;

    // Parse data block
    var dataEl = root.querySelector('[data-m12c-engrave-data]');
    try { this.data = JSON.parse(dataEl.textContent); }
    catch (e) { this.data = { variants: [], mainVariantIds: [], engravingVariantId: null }; }

    this.mainVariantIds = new Set((this.data.mainVariantIds || []).map(Number));
    this.savedValue = '';
    this.savedFile = null;
    this._syncing = false;
    this._passThrough = false;
    // Re-entrancy lock for the add-to-cart flow. The engraving line is pre-added
    // via an async fetch before the main product; without this lock a user who
    // clicks ATC again during that window (slow connection — nothing visible
    // happens yet) would start a second flow and Kalles would add the main
    // product TWICE (→ "main + engraving + main").
    this._adding = false;

    this._bindPopup();
    this._bindVariantChange();
    this._bindPopupSwatches();
    this._bindAddToCart();

    // Initial preview image for the currently selected variant
    this._updatePreview(this._currentVariantId());

    // Engraving is REQUIRED on these products: lock Add-to-Cart until a name is saved.
    this._updateAtcGate();

    // Expose to the global CartGuard so it can clear this UI after removing the line.
    activeController = this;
  }

  // ─────────────────────────────────────────
  //  Add-to-cart gate — require a saved engraving NAME (text) before ATC works
  // ─────────────────────────────────────────
  // Text-only requirement (an image-only engraving does NOT satisfy the gate).
  EngraveController.prototype._engravingTextSaved = function () {
    return !!(this.savedValue && String(this.savedValue).trim().length);
  };

  // The Add-to-Cart button(s) for this product (buy column first, page fallback).
  EngraveController.prototype._atcButtons = function () {
    var scope = (this.root.closest && this.root.closest('.rightSide')) || document;
    var btns = scope.querySelectorAll('.js_frm_cart');
    if (!btns.length) btns = document.querySelectorAll('.js_frm_cart');
    return btns;
  };

  // The hint shown under the locked button. Created once, re-created if Kalles
  // re-renders the button area and drops it.
  EngraveController.prototype._ensureHint = function () {
    if (this._hintEl && document.contains(this._hintEl)) return this._hintEl;
    var btns = this._atcButtons();
    if (!btns.length) { this._hintEl = null; return null; }
    var hint = document.createElement('div');
    hint.className = 'm12c-atc-hint';
    hint.setAttribute('data-m12c-atc-hint', '');
    hint.textContent = (this.data && this.data.atcHint) || 'Please add your engraving before adding to cart.';
    var first = btns[0];
    // Place the hint on its OWN line directly BELOW the button. The button usually sits
    // in a flex row, so inserting as a sibling would put the hint beside it — instead drop
    // it after that whole row (full-width block).
    var row = first.parentNode;
    if (row && row.parentNode) {
      row.parentNode.insertBefore(hint, row.nextSibling);
    } else if (row) {
      row.insertBefore(hint, first.nextSibling);
    }
    this._hintEl = hint;
    return hint;
  };

  // Toggle the greyed/locked state + hint based on whether a name is saved.
  EngraveController.prototype._updateAtcGate = function () {
    var met = this._engravingTextSaved();
    Array.prototype.forEach.call(this._atcButtons(), function (b) {
      b.classList.toggle('m12c-atc-locked', !met);
      if (met) b.removeAttribute('aria-disabled');
      else b.setAttribute('aria-disabled', 'true');
    });
    var hint = this._ensureHint();
    if (hint) hint.hidden = met;
  };

  // Nudge the customer: open the engraving popup and flash the hint.
  EngraveController.prototype._flashGate = function () {
    var openBtn = this.root.querySelector('[data-m12c-engrave-open]');
    if (openBtn) openBtn.click();
    var hint = this._ensureHint();
    if (hint) {
      hint.hidden = false;
      hint.classList.remove('m12c-atc-hint--flash');
      void hint.offsetWidth; // reflow so the animation restarts
      hint.classList.add('m12c-atc-hint--flash');
    }
  };

  EngraveController.prototype._q = function (sel) { return this.root.querySelector(sel); };

  // ── Current variant id (read Kalles' form, fall back to data block) ──
  EngraveController.prototype._currentVariantId = function () {
    var form = this._cartForm();
    var idInput = form ? form.querySelector('[name="id"]') : null;
    if (idInput && idInput.value) return parseInt(idInput.value, 10);
    return this.data.currentVariantId || null;
  };

  EngraveController.prototype._cartForm = function () {
    // Kalles main product form
    return document.querySelector('form.variations_form_ppr')
      || document.querySelector('#cart-form_ppr')
      || document.querySelector('form.variations_form');
  };

  // ─────────────────────────────────────────
  //  Preview image swap on variant change
  // ─────────────────────────────────────────
  EngraveController.prototype._variantById = function (id) {
    id = Number(id);
    return (this.data.variants || []).find(function (v) { return Number(v.id) === id; });
  };

  EngraveController.prototype._updatePreview = function (variantId) {
    var v = this._variantById(variantId);
    if (!v) return;
    var popupImg = this._q('.m12c__engrave-popup-img');
    var thumbImg = this._q('.m12c__engrave-thumb-img');
    if (v.backImg && popupImg) popupImg.src = v.backImg;
    if (v.backImgThumb && thumbImg) thumbImg.src = v.backImgThumb;
  };

  EngraveController.prototype._bindVariantChange = function () {
    var self = this;
    if ($K) {
      $K(document).on('variant:change', function (e, variant) {
        var id = variant && variant.id ? variant.id : self._currentVariantId();
        self._updatePreview(id);
        self._syncPopupSwatchActive(id);
        // Kalles can re-render the ATC button on variant change → re-apply the gate.
        self._updateAtcGate();
      });
    }
    // Fallback: native change on the form's id input
    var form = this._cartForm();
    if (form) {
      form.addEventListener('change', function () {
        self._updatePreview(self._currentVariantId());
      });
    }
  };

  // ─────────────────────────────────────────
  //  Popup swatches → drive Kalles' native picker
  // ─────────────────────────────────────────
  EngraveController.prototype._bindPopupSwatches = function () {
    var self = this;
    var group = this._q('[data-m12c-popup-swatches]');
    if (!group) return;
    group.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-m12c-popup-swatch]');
      if (!btn || btn.classList.contains('is-disabled')) return;
      var value = btn.dataset.value;
      self._selectKallesValue(value);
    });
  };

  // Click the matching native Kalles swatch so Kalles updates the variant + fires variant:change
  EngraveController.prototype._selectKallesValue = function (value) {
    var form = this._cartForm();
    if (!form) return;
    var swatches = form.querySelectorAll('.swatches-select .nt-swatch, .swatches-select li');
    var target = null;
    swatches.forEach(function (li) {
      if (target) return;
      var esc = li.getAttribute('data-escape');
      var val = li.getAttribute('data-value');
      if (esc === value || val === value) target = li;
    });
    if (target) {
      if ($K) { $K(target).trigger('click'); } else { target.click(); }
    } else {
      // Fallback: a <select name="id"> based picker
      var sel = form.querySelector('select[name="id"], .product-select_pr');
      if (sel && $K) { $K(sel).trigger('change'); }
    }
  };

  EngraveController.prototype._syncPopupSwatchActive = function (variantId) {
    var group = this._q('[data-m12c-popup-swatches]');
    if (!group) return;
    var v = this._variantById(variantId);
    if (!v || !v.options) return;
    var pos = parseInt(group.dataset.optionPosition, 10) || 1;
    var selected = v.options[pos - 1];
    group.querySelectorAll('[data-m12c-popup-swatch]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.dataset.value === selected);
    });
  };

  // ─────────────────────────────────────────
  //  Popup open / save / cancel / preview / upload
  //  (ported from Dawn _bindEngrave, theme-agnostic)
  // ─────────────────────────────────────────
  EngraveController.prototype._bindPopup = function () {
    var self = this;
    var root = this.root;
    var openBtn = root.querySelector('[data-m12c-engrave-open]');
    var popup = root.querySelector('[data-m12c-engrave-popup]');
    if (!openBtn || !popup) return;

    var input = popup.querySelector('[data-m12c-engrave-input]');
    var popupName = popup.querySelector('[data-m12c-engrave-popup-name]');
    var thumbName = openBtn.querySelector('[data-m12c-engrave-thumb-name]');
    var clearBtn = popup.querySelector('[data-m12c-engrave-clear]');
    var saveBtn = popup.querySelector('[data-m12c-engrave-save]');
    var termsCheckbox = popup.querySelector('[data-m12c-engrave-terms]');
    var cancelBtn = popup.querySelector('[data-m12c-engrave-cancel]');
    var closeBtns = popup.querySelectorAll('[data-m12c-engrave-close]');
    var propertyInput = root.querySelector('[data-m12c-engrave-property]');

    var fileInput = popup.querySelector('[data-m12c-engrave-image]');
    var uploadDrop = popup.querySelector('[data-m12c-engrave-upload-drop]');
    var uploadPreview = popup.querySelector('[data-m12c-engrave-upload-preview]');
    var uploadThumb = popup.querySelector('[data-m12c-engrave-upload-thumb]');
    var uploadName = popup.querySelector('[data-m12c-engrave-upload-name]');
    var uploadRemove = popup.querySelector('[data-m12c-engrave-upload-remove]');

    var MAX = 10;
    var calcSize = function (len, maxPx, minPx) {
      if (len === 0) return maxPx;
      return Math.max(minPx, Math.round(maxPx - (len / MAX) * (maxPx - minPx)));
    };

    var transientFile = null;
    var transientThumbUrl = null;

    var renderFileState = function (file) {
      if (transientThumbUrl) { URL.revokeObjectURL(transientThumbUrl); transientThumbUrl = null; }
      if (file) {
        transientThumbUrl = URL.createObjectURL(file);
        if (uploadThumb) uploadThumb.src = transientThumbUrl;
        if (uploadName) uploadName.textContent = file.name;
        if (uploadPreview) uploadPreview.removeAttribute('hidden');
        if (uploadDrop) uploadDrop.classList.add('has-file');
      } else {
        if (uploadThumb) uploadThumb.removeAttribute('src');
        if (uploadName) uploadName.textContent = '';
        if (uploadPreview) uploadPreview.setAttribute('hidden', '');
        if (uploadDrop) uploadDrop.classList.remove('has-file');
      }
    };

    var updatePreview = function (val) {
      var popupPx = calcSize(val.length, 20, 16);
      var thumbPx = calcSize(val.length, 8, 4);
      if (popupName) { popupName.textContent = val; popupName.style.fontSize = popupPx + 'px'; }
      if (thumbName) { thumbName.textContent = val; thumbName.style.fontSize = thumbPx + 'px'; }
      if (clearBtn) clearBtn.classList.toggle('is-visible', val.length > 0);
    };
    this._renderPreview = updatePreview;

    // SAVE is enabled only when the terms & conditions checkbox is ticked.
    var updateSaveState = function () {
      if (saveBtn) saveBtn.disabled = !!(termsCheckbox && !termsCheckbox.checked);
    };
    if (termsCheckbox) termsCheckbox.addEventListener('change', updateSaveState);
    updateSaveState();

    // Terms text is rich text (may contain a link). Open links in a new tab so the
    // popup isn't lost, and let clicking the text (but not a link) toggle the checkbox.
    var termsText = popup.querySelector('[data-m12c-engrave-terms-text]');
    if (termsText) {
      termsText.querySelectorAll('a').forEach(function (a) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener');
      });
      termsText.addEventListener('click', function (e) {
        if (e.target.closest('a')) return; // let links work normally
        if (termsCheckbox) { termsCheckbox.checked = !termsCheckbox.checked; updateSaveState(); }
      });
    }

    var openPopup = function () {
      transientFile = self.savedFile;
      renderFileState(transientFile);
      if (fileInput) fileInput.value = '';
      popup.removeAttribute('hidden');
      document.body.classList.add('m12c-no-scroll');
      if (input) { input.focus(); updatePreview(input.value); }
      updateSaveState();
    };
    var closePopup = function () {
      popup.setAttribute('hidden', '');
      document.body.classList.remove('m12c-no-scroll');
    };
    var revertOnDismiss = function () {
      if (input) { input.value = self.savedValue; updatePreview(self.savedValue); }
      transientFile = self.savedFile;
      renderFileState(transientFile);
      if (fileInput) fileInput.value = '';
    };

    openBtn.addEventListener('click', openPopup);
    closeBtns.forEach(function (btn) { btn.addEventListener('click', function () { revertOnDismiss(); closePopup(); }); });
    if (cancelBtn) cancelBtn.addEventListener('click', function () { revertOnDismiss(); closePopup(); });

    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        if (termsCheckbox && !termsCheckbox.checked) return; // must accept terms first
        self.savedValue = input ? input.value.trim() : '';
        self.savedFile = transientFile;
        if (propertyInput) propertyInput.value = self.savedValue;
        self._updateAtcGate(); // unlock ATC once a name is saved (or re-lock if cleared)
        closePopup();
      });
    }

    if (input) input.addEventListener('input', function () { updatePreview(input.value); });
    // Block now sits inside Kalles' product form after repositioning — don't let
    // Enter in the engraving field submit the form (i.e. add to cart).
    if (input) input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });
    if (clearBtn) clearBtn.addEventListener('click', function () { if (input) { input.value = ''; updatePreview(''); input.focus(); } });

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (!file) return;
        transientFile = file;
        renderFileState(transientFile);
      });
    }
    if (uploadRemove) {
      uploadRemove.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        transientFile = null;
        if (fileInput) fileInput.value = '';
        renderFileState(null);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.hasAttribute('hidden')) { revertOnDismiss(); closePopup(); }
    });
  };

  EngraveController.prototype._hasEngraving = function () {
    return !!(this.savedValue || this.savedFile) && !!this.data.engravingVariantId;
  };

  // ─────────────────────────────────────────
  //  Add to cart — pre-add engraving line, then let Kalles add the main item
  // ─────────────────────────────────────────
  EngraveController.prototype._bindAddToCart = function () {
    var self = this;
    // Capture phase so we run BEFORE Kalles' delegated bubble handler on body.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.js_frm_cart');
      if (!btn) return;
      // Re-fired click (we triggered it ourselves) → pass straight through to Kalles.
      // Checked FIRST so the re-fire is never re-evaluated against _hasEngraving/_adding.
      if (self._passThrough) { self._passThrough = false; return; }

      // Engraving is REQUIRED on these products: block the add until a NAME is saved,
      // and nudge the customer to the engraving popup. (Image-only does not satisfy this.)
      if (!self._engravingTextSaved()) {
        e.preventDefault();
        e.stopPropagation();
        self._flashGate();
        return;
      }

      // Always suppress the raw click — we either start the add flow or we're mid-add.
      e.preventDefault();
      e.stopPropagation();

      // Already adding → ignore this duplicate click. This is the guard against the
      // intermittent double main-product add caused by clicking ATC again while the
      // engraving pre-add (or Kalles' main add) is still in flight.
      if (self._adding) return;
      self._adding = true;
      // Visual feedback: spinner + disabled look so the customer sees the add is
      // in progress and isn't tempted to click again during the pre-add network call.
      btn.classList.add('m12c-adding');

      // Engraving is added a moment before the main product → open a grace window so
      // the guard doesn't treat the freshly-added engraving line as orphaned.
      addGraceUntil = Date.now() + 6000;

      var form = btn.closest('form') || self._cartForm();
      var mainVariantId = self._currentVariantId();
      // Track the engraving ON the main product line too (Kalles serialises the
      // form on the re-fired add, so these hidden inputs ride along).
      self._setMainFormProperty(form, self.savedValue);
      // Hidden marker pairing this camera line to its engraving line. Set whenever
      // engraving is saved (even image-only, where there's no name) so the guard can
      // reliably detect & remove the camera if its engraving line is later removed.
      self._setMainFormMarker(form, true);

      self._addEngravingLine(mainVariantId)
        .catch(function (err) { console.error('[Engrave] add line failed', err); })
        .then(function () {
          // Re-fire the click; this time pass through to Kalles, which adds the
          // main product and refreshes/opens its drawer (engraving already in cart).
          self._passThrough = true;
          btn.click();

          // Hold the lock until the cart actually settles, so a slow Kalles main-add
          // can't be double-fired by another click either. Release on the next cart
          // update event, with a timeout fallback so the button can never get stuck.
          var released = false;
          var release = function () {
            if (released) return;
            released = true;
            self._adding = false;
            btn.classList.remove('m12c-adding');
          };
          if ($K) { $K(document).one('CartUpdateSuccess CartChangeSuccess', release); }
          setTimeout(release, 4000);
        });
    }, true);
  };

  // Adds/updates a hidden `properties[Engraving]` input on Kalles' product form so the
  // engraving name is recorded on the MAIN product line too (for tracking).
  EngraveController.prototype._setMainFormProperty = function (form, name) {
    if (!form) return;
    var input = form.querySelector('input[data-engrave-main-prop]');
    if (!name) { if (input && input.parentNode) input.parentNode.removeChild(input); return; }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Engraving]';
      input.setAttribute('data-engrave-main-prop', '');
      form.appendChild(input);
    }
    input.value = name;
  };

  // Adds/removes a hidden `properties[_has_engraving]` marker on Kalles' product form.
  // `_`-prefixed → hidden from cart/checkout display. The CartGuard uses it to pair the
  // camera line with its engraving line for two-way removal (image-only engravings have
  // no `Engraving` name, so we can't rely on that property alone).
  EngraveController.prototype._setMainFormMarker = function (form, on) {
    if (!form) return;
    var input = form.querySelector('input[data-engrave-main-marker]');
    if (!on) { if (input && input.parentNode) input.parentNode.removeChild(input); return; }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_has_engraving]';
      input.setAttribute('data-engrave-main-marker', '');
      form.appendChild(input);
    }
    input.value = '1';
  };

  EngraveController.prototype._addEngravingLine = function (mainVariantId) {
    var engId = this.data.engravingVariantId;
    if (!engId) return Promise.resolve();
    var name = this.savedValue || '';
    var file = this.savedFile || null;

    // The engraving line records `_for_variant` (the main variant it belongs to) so the
    // global guard can remove THIS line precisely when that product leaves the cart.
    // `Engraving` / `Engraving Image` are shown under the price (snippets/cart_items.liquid).
    var opts;
    if (file) {
      var fd = new FormData();
      fd.append('items[0][id]', engId);
      fd.append('items[0][quantity]', 1);
      if (name) fd.append('items[0][properties][Engraving]', name);
      if (mainVariantId) fd.append('items[0][properties][_for_variant]', mainVariantId);
      fd.append('items[0][properties][Engraving Image]', file, file.name);
      opts = { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd };
    } else {
      var props = {};
      if (name) props['Engraving'] = name;
      if (mainVariantId) props['_for_variant'] = String(mainVariantId);
      opts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: engId, quantity: 1, properties: props }] })
      };
    }
    return fetch('/cart/add.js', opts).then(function (r) { return r.json(); });
  };

  // ─────────────────────────────────────────
  //  Global cart guard — runs on EVERY page (engraving.js is loaded globally
  //  via theme.liquid). Removes the engraving line item whenever NONE of the
  //  engraving-eligible products (Theme settings → Engraving → product IDs)
  //  remain in the cart. This covers removing the camera from a different page.
  // ─────────────────────────────────────────
  function debounce(fn, delay) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, delay); };
  }

  function readConfig() {
    var el = document.getElementById('engraving-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function CartGuard(cfg) {
    this.engId = Number(cfg.engravingVariantId);
    this.eligible = new Set((cfg.eligibleProductIds || []).map(Number).filter(Boolean));
    this._syncing = false;
    this._pending = false;
    if (!this.engId) return; // not configured → do nothing

    var self = this;
    var jq = window.jQuery_T4NT || window.jQuery || window.$ || null;
    // 350ms debounce lets Kalles commit its own cart change before we read /cart.js.
    var check = debounce(function () { self._run(); }, 350);

    if (jq) {
      jq(document).on('CartUpdateSuccess CartChangeSuccess', check);
      jq('body').on('CartUpdateSuccess CartChangeSuccess', check);
    }
    // Direct hook on Kalles' remove buttons — guarantees a check fires on item removal.
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.js_cart_rem, .cart_ac_remove')) check();
    }, true);

    var attach = function () {
      var drawer = document.querySelector('#nt_cart_canvas')
        || document.querySelector('.nt_js_cart')
        || document.querySelector('.js_cat_items');
      if (drawer) new MutationObserver(check).observe(drawer, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach);
    else attach();
  }

  // Run a sync, but if one is already in flight, requeue so we always re-check the
  // SETTLED cart afterwards (prevents the "checked too early, dropped the retry" race).
  CartGuard.prototype._run = function () {
    if (this._syncing) { this._pending = true; return; }
    this._sync();
  };

  CartGuard.prototype._sync = function () {
    var self = this;
    if (this._syncing) return;
    this._syncing = true;

    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var engLines = cart.items.filter(function (it) { return Number(it.variant_id) === self.engId; });

        var variantIdsInCart = {};
        var productIdsInCart = [];
        cart.items.forEach(function (it) {
          variantIdsInCart[Number(it.variant_id)] = true;
          productIdsInCart.push(Number(it.product_id));
        });

        // Main variant ids that currently have an engraving line linked to them.
        var engForVariants = {};
        engLines.forEach(function (line) {
          var props = line.properties || {};
          var fv = props._for_variant || props['_for_variant'];
          if (fv) engForVariants[Number(fv)] = true;
        });

        var toRemove = [];

        // FORWARD: an engraving line whose paired camera left the cart → remove the engraving line.
        engLines.forEach(function (line) {
          var props = line.properties || {};
          var forVariant = props._for_variant || props['_for_variant'];
          var orphan;
          if (forVariant) {
            // Primary: the line is linked to a specific main variant.
            orphan = !variantIdsInCart[Number(forVariant)];
          } else if (self.eligible.size) {
            // Fallback (legacy lines without _for_variant): remove only if none of the
            // eligible products are present.
            orphan = !productIdsInCart.some(function (pid) { return self.eligible.has(pid); });
          } else {
            orphan = false; // can't determine → keep (safe)
          }
          if (orphan) toRemove.push(line);
        });

        // REVERSE: a camera line marked `_has_engraving` whose paired engraving line is gone
        // → remove the WHOLE camera line (two-way bonded pair). Keyed strictly off the
        // explicit marker so legacy carts (no marker) are never affected.
        cart.items.forEach(function (it) {
          if (Number(it.variant_id) === self.engId) return; // skip engraving service lines
          var props = it.properties || {};
          var marked = props._has_engraving || props['_has_engraving'];
          if (!marked) return;
          if (!engForVariants[Number(it.variant_id)]) toRemove.push(it);
        });

        console.log('[Engrave] guard check — engraving lines:', engLines.length, '| lines to remove (both directions):', toRemove.length);
        if (!toRemove.length) return;

        // Defer during the add grace window (engraving line is added just before its
        // main product, so it briefly looks orphaned). Re-check once the window closes
        // so a genuine orphan is still cleaned up.
        if (Date.now() < addGraceUntil) {
          console.log('[Engrave] guard — within add grace window, deferring removal');
          setTimeout(function () { self._run(); }, (addGraceUntil - Date.now()) + 300);
          return;
        }

        var chain = Promise.resolve();
        toRemove.forEach(function (line) {
          chain = chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: line.key, quantity: 0, sections: 'cart_js' })
            }).then(function (r) { return r.json(); });
          });
        });
        return chain.then(function (res) {
          if (activeController) activeController._clearUI();

          // 1) Native refresh — updates the count + totals the way Kalles does.
          try {
            if (window.geckoShopify && typeof window.geckoShopify.onCartUpdate === 'function' && res && res.sections && res.sections.cart_js) {
              window.geckoShopify.onCartUpdate(0, 0, res.sections.cart_js);
            } else {
              console.warn('[Engrave] geckoShopify.onCartUpdate unavailable — relying on DOM cleanup');
            }
          } catch (e) { console.error('[Engrave] onCartUpdate error', e); }

          // 2) Guaranteed visual cleanup — remove the now-deleted engraving line node(s)
          //    from the drawer DOM so a stale "ghost" line never lingers, even if the
          //    native re-render misses the items list. Line node class is cart_item_<id>.
          toRemove.forEach(function (line) {
            document.querySelectorAll('.cart_item_' + line.id).forEach(function (node) {
              if (node.parentNode) node.parentNode.removeChild(node);
            });
          });
          console.log('[Engrave] removed orphaned engraving line(s) server-side + cleared from drawer DOM');
        });
      })
      .catch(function (err) { console.error('[Engrave] guard sync error', err); })
      .then(function () {
        self._syncing = false;
        if (self._pending) { self._pending = false; self._run(); } // re-check the settled cart
      });
  };

  EngraveController.prototype._clearUI = function () {
    this.savedValue = '';
    this.savedFile = null;
    var prop = this.root.querySelector('[data-m12c-engrave-property]');
    if (prop) prop.value = '';
    var thumbName = this.root.querySelector('[data-m12c-engrave-thumb-name]');
    if (thumbName) { thumbName.textContent = ''; thumbName.style.fontSize = ''; }
    var input = this.root.querySelector('[data-m12c-engrave-input]');
    if (input) input.value = '';
    var popupName = this.root.querySelector('[data-m12c-engrave-popup-name]');
    if (popupName) { popupName.textContent = ''; popupName.style.fontSize = ''; }
    var clearBtn = this.root.querySelector('[data-m12c-engrave-clear]');
    if (clearBtn) clearBtn.classList.remove('is-visible');
    // Engraving removed → re-lock Add-to-Cart.
    this._updateAtcGate();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
